import { type FC, memo, useState, useCallback, useEffect } from 'react';
import styles from './index.module.scss';
import SwapSection from './swap-section';
import { useModel } from 'foca';
import { swapCoinModel } from '@models/swap-coin.model';
import SwapExchange from './swap-exchange';
import SwapSubmit from './swap-submit';
import RouteInfo from '../swap-route';
import { useHash, useToggle } from 'react-use';
import { findLocationSearch } from '@utils/location-search';
import chartIcon from '@assets/images/parrot/chart.svg';
import SelectSlippage from '@components/select-slippage';

import { useParrotBestRoutes } from '@hooks/use-parrot-best-routes';
import { useParrotCoin } from '@hooks/use-parrot-coins';
import TokenInfo from './token-info';
import SwapChart from './chart';

const Swap: FC = () => {
  const [visible, toggleVisible] = useToggle(false);
  const { coinTypeA, coinTypeB } = useModel(swapCoinModel);
  const [amountSpecifiedIsInput, setAmountSpecifiedIsInput] = useState(false);
  const [fromAmount, setFromAmount] = useState<string>();
  const [toAmount, setToAmount] = useState<string>();

  const hash = useHash();
  const { coin: coinA } = useParrotCoin(coinTypeA);
  const { coin: coinB } = useParrotCoin(coinTypeB);

  const amount = amountSpecifiedIsInput ? fromAmount : toAmount;

  useEffect(() => {
    if (!amount) {
      if (amountSpecifiedIsInput) {
        setToAmount(undefined);
      } else {
        setFromAmount(undefined);
      }
    }
  }, [amount, amountSpecifiedIsInput]);

  const { bestRoute, loading, notLiquidity, notRoute } = useParrotBestRoutes(
    coinTypeA,
    coinTypeB,
    amount,
    amountSpecifiedIsInput,
  );

  const handleTransacted = useCallback(() => {
    setFromAmount(undefined);
    setToAmount(undefined);
    // TO clear both input value
  }, []);

  const handleSetInput = useCallback((value?: string) => {
    setFromAmount(value);
    setAmountSpecifiedIsInput(true);
  }, []);

  const handleSetOutput = useCallback((value?: string) => {
    setToAmount(value);
    setAmountSpecifiedIsInput(false);
  }, []);

  const handleExchange = useCallback(() => {
    swapCoinModel.exchange();
  }, []);

  useEffect(() => {
    if (coinTypeA && coinTypeB) {
      window.location.hash = `/swap?from=${coinTypeA}&to=${coinTypeB}`;
    }
  }, [coinTypeA, coinTypeB]);

  useEffect(() => {
    const _hash = hash[0].replace(/.*\?/, '');
    const input = findLocationSearch(_hash, 'from');
    const output = findLocationSearch(_hash, 'to');

    input && swapCoinModel.updateA(input);
    output && swapCoinModel.updateB(output);
  }, [hash[0]]);

  useEffect(() => {
    if (bestRoute?.amount_out && bestRoute?.amount_in && !notLiquidity) {
      if (amountSpecifiedIsInput) {
        setToAmount(bestRoute.amount_out);
      } else {
        setFromAmount(bestRoute.amount_in);
      }
    }
  }, [amountSpecifiedIsInput, bestRoute?.amount_out, bestRoute?.amount_in, notLiquidity]);

  useEffect(() => {
    if (loading) {
      if (amountSpecifiedIsInput) {
        setToAmount(undefined);
      } else {
        setFromAmount(undefined);
      }
    }
  }, [loading]);

  return (
    <div className={styles.main}>
      {visible && (
        <div className={styles.aside}>
          <div className={styles.chart}>
            <SwapChart coinA={coinA} coinB={coinB} />
          </div>
          <TokenInfo coinA={coinA} coinB={coinB} />
        </div>
      )}
      <div className={styles.article}>
        <div className={styles.wrapper}>
          <div className={styles.wrapper_con}>
            <div className={styles.top}>
              <div className={styles.title}>Swap</div>
              <div className={styles.right}>
                <div className={styles.chart_icon}>
                  <img src={chartIcon} onClick={toggleVisible} />
                </div>
                <SelectSlippage />
              </div>
            </div>
            <div className={styles.middle}>
              <SwapSection
                label="Pay"
                coin={coinA}
                onSelectCoin={swapCoinModel.updateA}
                disableCoins={coinTypeB}
                showMaxAmount={true}
                amount={fromAmount}
                inputByUser={amountSpecifiedIsInput}
                onChangeValue={handleSetInput}
                fetchLoading={loading}
              />

              <SwapExchange onExchange={handleExchange} />
              <div className={styles.space}></div>
              <SwapSection
                label="Receive"
                coin={coinB}
                onSelectCoin={swapCoinModel.updateB}
                disableCoins={coinTypeA}
                showMaxAmount={false}
                amount={toAmount}
                inputByUser={!amountSpecifiedIsInput}
                onChangeValue={handleSetOutput}
                fetchLoading={loading}
              />
            </div>
          </div>
        </div>
        <SwapSubmit
          isLiquidity={notLiquidity}
          isRoute={notRoute}
          bestRoute={bestRoute}
          coinA={coinA}
          coinB={coinB}
          fetchLoading={loading}
          amount={amount}
          onTransacted={handleTransacted}
        />

        {coinA && coinB && !notLiquidity && amount && !notRoute && bestRoute && (
          <RouteInfo bestRoute={bestRoute} coinA={coinA} coinB={coinB} loading={loading} />
        )}
      </div>
    </div>
  );
};

export default memo(Swap);
